import { useCallback, useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateClickArg } from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import esLocale from '@fullcalendar/core/locales/es';
import type { DatesSetArg, EventClickArg } from '@fullcalendar/core';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Cancel,
  ContentCopy,
  EventBusy,
  CalendarMonth,
  Delete,
  Edit,
  EventAvailable,
  HelpOutline,
  InfoOutlined,
  Person,
  Phone,
  Refresh,
  RestartAlt,
  Save,
  Schedule,
  Tune,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import DetailHeader from '../components/DetailHeader';
import { usePermissions } from '../contexts/PermissionContext';
import { useLanguage } from '../hooks/useLanguage';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import {
  cancelAppointment,
  createAppointment,
  createAppointmentCalendar,
  createAvailabilityException,
  createAvailabilityRule,
  cloneAppointmentCalendar,
  deleteAvailabilityException,
  fetchAppointmentCalendar,
  listAppointments,
  listAvailabilityExceptions,
  listAvailabilityRules,
  updateAppointment,
  updateAppointmentCalendar,
  updateAvailabilityException,
  updateAvailabilityRule,
} from '../services/appointments';
import type {
  Appointment,
  AppointmentCalendar,
  AppointmentCalendarClonePayload,
  AppointmentCalendarPayload,
  AppointmentPayload,
  AppointmentStatus,
  AvailabilityException,
  AvailabilityExceptionPayload,
  AvailabilityExceptionType,
  AvailabilityRule,
  AvailabilityRulePayload,
} from '../types';

const WEEKDAY_VALUES = [0, 1, 2, 3, 4, 5, 6] as const;
const DEFAULT_SLOT_MINUTES = 15;
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const SLOT_INPUT_PROPS = { min: 5, max: 480, step: 5 };
const NOTICE_INPUT_PROPS = { min: 0, step: 5 };
const DAYS_AHEAD_INPUT_PROPS = { min: 1, max: 365, step: 1 };
const DAYJS_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: 'en',
  es: 'es',
  'pt-BR': 'pt-br',
};
const FULL_CALENDAR_LOCALE_BY_LANGUAGE: Record<string, string> = {
  en: 'en',
  es: 'es',
  'pt-BR': 'pt-br',
};

type Weekday = (typeof WEEKDAY_VALUES)[number];

interface CalendarFormState {
  name: string;
  description: string;
  timezone: string;
  isActive: boolean;
  minSlotDurationMinutes: number;
  defaultSlotDurationMinutes: number;
  maxSlotDurationMinutes: number;
  slotStepMinutes: number;
  minNoticeMinutes: number;
  maxDaysAhead: number;
}

interface ExceptionDialogState {
  open: boolean;
  exception: AvailabilityException | null;
  values: AvailabilityExceptionPayload;
}

interface CloneDialogState {
  open: boolean;
  name: string;
  includeFutureExceptions: boolean;
  includeBlocks: boolean;
}

interface AppointmentDialogState {
  open: boolean;
  date: string;
  startTime: string;
  durationMinutes: number;
  contactName: string;
  contactPhoneE164: string;
  contactEmail: string;
}

interface AppointmentRangeState {
  startsAtFrom: string;
  startsAtTo: string;
}

interface AppointmentDetailsFormState {
  contactName: string;
  contactPhoneE164: string;
  contactEmail: string;
}

interface WeeklyWindowEntry {
  id: string;
  days: Weekday[];
  startTime: string;
  endTime: string;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  label: string;
}

function createEmptyCalendarForm(): CalendarFormState {
  return {
    name: '',
    description: '',
    timezone: DEFAULT_TIMEZONE,
    isActive: true,
    minSlotDurationMinutes: DEFAULT_SLOT_MINUTES,
    defaultSlotDurationMinutes: DEFAULT_SLOT_MINUTES,
    maxSlotDurationMinutes: DEFAULT_SLOT_MINUTES,
    slotStepMinutes: DEFAULT_SLOT_MINUTES,
    minNoticeMinutes: 0,
    maxDaysAhead: 30,
  };
}

function createExceptionPayload(): AvailabilityExceptionPayload {
  const today = new Date().toISOString().slice(0, 10);
  return {
    date: today,
    startTime: null,
    endTime: null,
    exceptionType: 'blocked',
    reason: '',
  };
}

function createClosedCloneDialogState(): CloneDialogState {
  return {
    open: false,
    name: '',
    includeFutureExceptions: true,
    includeBlocks: true,
  };
}

function createClosedAppointmentDialogState(defaultDurationMinutes = DEFAULT_SLOT_MINUTES): AppointmentDialogState {
  return {
    open: false,
    date: dayjs().format('YYYY-MM-DD'),
    startTime: '09:00',
    durationMinutes: defaultDurationMinutes,
    contactName: '',
    contactPhoneE164: '',
    contactEmail: '',
  };
}

function createAppointmentDialogState(
  defaultDurationMinutes: number,
  selectedDate?: Date,
  allDay = false,
): AppointmentDialogState {
  const selected = selectedDate ? dayjs(selectedDate) : dayjs();
  return {
    open: true,
    date: selected.format('YYYY-MM-DD'),
    startTime: allDay ? '09:00' : selected.format('HH:mm'),
    durationMinutes: defaultDurationMinutes,
    contactName: '',
    contactPhoneE164: '',
    contactEmail: '',
  };
}

function appointmentToDetailsForm(appointment: Appointment): AppointmentDetailsFormState {
  return {
    contactName: appointment.contactName || '',
    contactPhoneE164: appointment.contactPhoneE164 || '',
    contactEmail: appointment.contactEmail || '',
  };
}

/**
 * Extract a readable DRF error from Axios failures.
 * Error responses are not normalized by the success interceptor, so this accepts
 * both snake_case and camelCase keys before falling back to the generic message.
 */
function getAppointmentApiErrorMessage(error: unknown, fallback: string): string {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return fallback;
  }
  const data = responseData as Record<string, unknown>;
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const nonFieldErrors = data.non_field_errors ?? data.nonFieldErrors;
  if (Array.isArray(nonFieldErrors) && nonFieldErrors.length > 0) {
    return String(nonFieldErrors[0]);
  }
  for (const value of Object.values(data)) {
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value) && value.length > 0) {
      return String(value[0]);
    }
  }
  return fallback;
}

function createWindowEntryId() {
  return `weekly-window-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function createWeeklyWindowEntry(
  days: Weekday[],
  startTime = '09:00',
  endTime = '18:00',
): WeeklyWindowEntry {
  return {
    id: createWindowEntryId(),
    days,
    startTime,
    endTime,
    effectiveFrom: null,
    effectiveUntil: null,
    label: '',
  };
}

function createDefaultWeeklyWindows(): WeeklyWindowEntry[] {
  return [
    createWeeklyWindowEntry([0, 1, 2, 3, 4], '09:00', '18:00'),
    createWeeklyWindowEntry([5], '09:00', '14:00'),
  ];
}

function calendarToForm(calendar: AppointmentCalendar): CalendarFormState {
  return {
    name: calendar.name,
    description: calendar.description || '',
    timezone: calendar.timezone || DEFAULT_TIMEZONE,
    isActive: Boolean(calendar.isActive),
    minSlotDurationMinutes: calendar.minSlotDurationMinutes,
    defaultSlotDurationMinutes: calendar.defaultSlotDurationMinutes,
    maxSlotDurationMinutes: calendar.maxSlotDurationMinutes,
    slotStepMinutes: calendar.slotStepMinutes,
    minNoticeMinutes: calendar.minNoticeMinutes,
    maxDaysAhead: calendar.maxDaysAhead,
  };
}

function buildCalendarPayload(form: CalendarFormState): AppointmentCalendarPayload {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
    timezone: form.timezone.trim() || DEFAULT_TIMEZONE,
    isActive: form.isActive,
    minSlotDurationMinutes: Number(form.minSlotDurationMinutes),
    defaultSlotDurationMinutes: Number(form.defaultSlotDurationMinutes),
    maxSlotDurationMinutes: Number(form.maxSlotDurationMinutes),
    slotStepMinutes: Number(form.slotStepMinutes),
    minNoticeMinutes: Number(form.minNoticeMinutes),
    maxDaysAhead: Number(form.maxDaysAhead),
    bookingPolicy: {},
  };
}

function shouldUseTwelveHourClock(locale: string) {
  return Boolean(new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12);
}

function isWeekday(value: number): value is Weekday {
  return WEEKDAY_VALUES.includes(value as Weekday);
}

function getAppointmentDisplayName(appointment: Appointment, fallback: string) {
  return appointment.contactName || appointment.linkedContactName || appointment.contactPhoneE164 || fallback;
}

function getAppointmentStatusColor(statusValue: AppointmentStatus): 'success' | 'default' | 'warning' | 'info' {
  if (statusValue === 'scheduled') {
    return 'success';
  }
  if (statusValue === 'cancelled') {
    return 'default';
  }
  if (statusValue === 'no_show') {
    return 'warning';
  }
  return 'info';
}

function buildRuleGroupingKey(rule: AvailabilityRule) {
  return [
    rule.startTime,
    rule.endTime,
    rule.effectiveFrom || '',
    rule.effectiveUntil || '',
    rule.label || '',
  ].join('|');
}

function sortWeekdayRules(left: AvailabilityRule, right: AvailabilityRule) {
  if (left.startTime !== right.startTime) {
    return left.startTime.localeCompare(right.startTime);
  }
  return left.endTime.localeCompare(right.endTime);
}

function buildWeeklyWindowsFromRules(source: AvailabilityRule[]): WeeklyWindowEntry[] {
  const grouped = new Map<string, WeeklyWindowEntry>();

  source.filter((rule) => rule.isActive).forEach((rule) => {
    if (!isWeekday(rule.weekday)) {
      return;
    }

    const key = buildRuleGroupingKey(rule);
    const existing = grouped.get(key);
    if (existing) {
      existing.days = [...existing.days, rule.weekday].sort((left, right) => left - right);
      return;
    }

    grouped.set(key, {
      id: createWindowEntryId(),
      days: [rule.weekday],
      startTime: rule.startTime,
      endTime: rule.endTime,
      effectiveFrom: rule.effectiveFrom,
      effectiveUntil: rule.effectiveUntil,
      label: rule.label || '',
    });
  });

  return [...grouped.values()].sort((left, right) => {
    const leftDay = left.days[0] ?? 99;
    const rightDay = right.days[0] ?? 99;
    if (leftDay !== rightDay) {
      return leftDay - rightDay;
    }
    return left.startTime.localeCompare(right.startTime);
  });
}

function buildWeeklyWindowPayloads(windows: WeeklyWindowEntry[]) {
  const payloadsByWeekday = new Map<Weekday, AvailabilityRulePayload[]>();
  WEEKDAY_VALUES.forEach((weekday) => payloadsByWeekday.set(weekday, []));

  windows.forEach((windowEntry) => {
    windowEntry.days.forEach((weekday) => {
      payloadsByWeekday.get(weekday)?.push({
        weekday,
        startTime: windowEntry.startTime,
        endTime: windowEntry.endTime,
        isActive: true,
        effectiveFrom: windowEntry.effectiveFrom,
        effectiveUntil: windowEntry.effectiveUntil,
        label: windowEntry.label,
      });
    });
  });

  payloadsByWeekday.forEach((payloads) => {
    payloads.sort((left, right) => {
      if (left.startTime !== right.startTime) {
        return left.startTime.localeCompare(right.startTime);
      }
      return left.endTime.localeCompare(right.endTime);
    });
  });

  return payloadsByWeekday;
}

function FieldLabelWithHelp({ label, help }: { label: string; help: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{label}</span>
      <Tooltip title={help} arrow>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

export default function AppointmentCalendarFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isNew = !id;
  const canRead = hasPermission('appointments_calendars_read');
  const canManageCalendars = hasPermission('appointments_calendars_manage');
  const canManageBlocks = hasPermission('appointments_blocks_manage');
  const canReadAppointments = hasPermission('appointments_read');
  const canCreateAppointments = hasPermission('appointments_create');
  const canUpdateAppointments = hasPermission('appointments_update');
  const canDeleteAppointments = hasPermission('appointments_delete');
  const { language } = useLanguage();
  const { locale } = useNumberFormat();
  const dayjsLocale = DAYJS_LOCALE_BY_LANGUAGE[language] || 'en';
  const fullCalendarLocale = FULL_CALENDAR_LOCALE_BY_LANGUAGE[language] || 'en';
  const useTwelveHourClock = shouldUseTwelveHourClock(locale);

  const [calendar, setCalendar] = useState<AppointmentCalendar | null>(null);
  const [form, setForm] = useState<CalendarFormState>(() => createEmptyCalendarForm());
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [weeklyWindows, setWeeklyWindows] = useState<WeeklyWindowEntry[]>(() => createDefaultWeeklyWindows());
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsRange, setAppointmentsRange] = useState<AppointmentRangeState | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [appointmentSaving, setAppointmentSaving] = useState(false);
  const [appointmentUpdating, setAppointmentUpdating] = useState(false);
  const [appointmentCancelling, setAppointmentCancelling] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exceptionDialog, setExceptionDialog] = useState<ExceptionDialogState>({
    open: false,
    exception: null,
    values: createExceptionPayload(),
  });
  const [cloneDialog, setCloneDialog] = useState<CloneDialogState>(() => createClosedCloneDialogState());
  const [appointmentDialog, setAppointmentDialog] = useState<AppointmentDialogState>(() => (
    createClosedAppointmentDialogState()
  ));
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentDetailsForm, setAppointmentDetailsForm] = useState<AppointmentDetailsFormState>({
    contactName: '',
    contactPhoneE164: '',
    contactEmail: '',
  });
  const [cancellationReason, setCancellationReason] = useState('');

  const pageTitle = isNew ? t('pages:appointments.form.newTitle') : t('pages:appointments.form.editTitle');
  usePageTitle(pageTitle, calendar?.name);

  const weekdayLabel = useCallback(
    (weekday: number) => t(`pages:appointments.weekdays.${weekday}`),
    [t],
  );

  const loadCalendar = useCallback(async () => {
    if (isNew || !id) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchAppointmentCalendar(id);
      setCalendar(payload);
      setForm(calendarToForm(payload));
    } catch (requestError: unknown) {
      console.error('Error loading appointment calendar:', requestError);
      setError(t('pages:appointments.errors.loadDetail'));
    } finally {
      setLoading(false);
    }
  }, [id, isNew, t]);

  const loadRules = useCallback(async () => {
    if (!id) {
      return;
    }
    setRulesLoading(true);
    try {
      const payload = await listAvailabilityRules(id);
      setRules(payload);
      setWeeklyWindows(buildWeeklyWindowsFromRules(payload));
    } catch (requestError: unknown) {
      console.error('Error loading appointment rules:', requestError);
      setError(t('pages:appointments.errors.loadRules'));
    } finally {
      setRulesLoading(false);
    }
  }, [id, t]);

  const loadExceptions = useCallback(async () => {
    if (!id) {
      return;
    }
    setExceptionsLoading(true);
    try {
      setExceptions(await listAvailabilityExceptions(id));
    } catch (requestError: unknown) {
      console.error('Error loading appointment exceptions:', requestError);
      setError(t('pages:appointments.errors.loadExceptions'));
    } finally {
      setExceptionsLoading(false);
    }
  }, [id, t]);

  const loadAppointments = useCallback(async (range: AppointmentRangeState | null) => {
    if (!id || !canReadAppointments || !range) {
      return;
    }
    setAppointmentsLoading(true);
    try {
      const payload = await listAppointments({
        calendarId: id,
        startsAtFrom: range.startsAtFrom,
        startsAtTo: range.startsAtTo,
        pageSize: 500,
      });
      setAppointments(payload.results);
    } catch (requestError: unknown) {
      console.error('Error loading appointments:', requestError);
      setError(t('pages:appointments.errors.loadAppointments'));
    } finally {
      setAppointmentsLoading(false);
    }
  }, [canReadAppointments, id, t]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    if (!isNew && id) {
      loadRules();
      loadExceptions();
    }
  }, [id, isNew, loadExceptions, loadRules]);

  useEffect(() => {
    if (appointmentsRange) {
      loadAppointments(appointmentsRange);
    }
  }, [appointmentsRange, loadAppointments]);

  const handleCalendarDatesSet = useCallback((rangeInfo: DatesSetArg) => {
    const nextRange = {
      startsAtFrom: rangeInfo.startStr,
      startsAtTo: rangeInfo.endStr,
    };
    setAppointmentsRange((currentRange) => {
      if (
        currentRange?.startsAtFrom === nextRange.startsAtFrom
        && currentRange?.startsAtTo === nextRange.startsAtTo
      ) {
        return currentRange;
      }
      return nextRange;
    });
  }, []);

  const openAppointmentDialog = useCallback((selectedDate?: Date, allDay = false) => {
    const duration = calendar?.defaultSlotDurationMinutes || form.defaultSlotDurationMinutes || DEFAULT_SLOT_MINUTES;
    setError(null);
    setSuccess(null);
    setAppointmentDialog(createAppointmentDialogState(duration, selectedDate, allDay));
  }, [calendar?.defaultSlotDurationMinutes, form.defaultSlotDurationMinutes]);

  const closeAppointmentDialog = useCallback(() => {
    if (appointmentSaving) {
      return;
    }
    const duration = calendar?.defaultSlotDurationMinutes || form.defaultSlotDurationMinutes || DEFAULT_SLOT_MINUTES;
    setAppointmentDialog(createClosedAppointmentDialogState(duration));
  }, [appointmentSaving, calendar?.defaultSlotDurationMinutes, form.defaultSlotDurationMinutes]);

  const handleCreateAppointment = async () => {
    if (!id || !canCreateAppointments) {
      setError(t('pages:appointments.accessDeniedCreate'));
      return;
    }
    if (!appointmentDialog.date || !appointmentDialog.startTime) {
      setError(t('pages:appointments.calendar.dateTimeRequired'));
      return;
    }
    setAppointmentSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: AppointmentPayload = {
        calendarId: id,
        date: appointmentDialog.date,
        startTime: appointmentDialog.startTime,
        durationMinutes: Number(appointmentDialog.durationMinutes),
        contactName: appointmentDialog.contactName.trim(),
        contactPhoneE164: appointmentDialog.contactPhoneE164.trim(),
        contactEmail: appointmentDialog.contactEmail.trim(),
      };
      await createAppointment(payload);
      setAppointmentDialog(createClosedAppointmentDialogState(payload.durationMinutes || DEFAULT_SLOT_MINUTES));
      await loadAppointments(appointmentsRange);
      setSuccess(t('pages:appointments.calendar.createSuccess'));
    } catch (requestError: unknown) {
      console.error('Error creating appointment:', requestError);
      setError(getAppointmentApiErrorMessage(requestError, t('pages:appointments.errors.createAppointment')));
    } finally {
      setAppointmentSaving(false);
    }
  };

  const openAppointmentDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentDetailsForm(appointmentToDetailsForm(appointment));
    setCancellationReason('');
  };

  const handleAppointmentEventClick = (eventInfo: EventClickArg) => {
    const appointment = appointments.find((entry) => entry.id === eventInfo.event.id);
    if (appointment) {
      openAppointmentDetails(appointment);
    }
  };

  const handleAppointmentDateClick = (dateInfo: DateClickArg) => {
    if (!canCreateAppointments) {
      return;
    }
    openAppointmentDialog(dateInfo.date, dateInfo.allDay);
  };

  const handleUpdateAppointment = async () => {
    if (!selectedAppointment || !canUpdateAppointments) {
      setError(t('pages:appointments.accessDeniedUpdate'));
      return;
    }
    setAppointmentUpdating(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateAppointment(selectedAppointment.id, {
        contactName: appointmentDetailsForm.contactName.trim(),
        contactPhoneE164: appointmentDetailsForm.contactPhoneE164.trim(),
        contactEmail: appointmentDetailsForm.contactEmail.trim(),
      });
      setSelectedAppointment(updated);
      setAppointmentDetailsForm(appointmentToDetailsForm(updated));
      await loadAppointments(appointmentsRange);
      setSuccess(t('pages:appointments.calendar.updateSuccess'));
    } catch (requestError: unknown) {
      console.error('Error updating appointment:', requestError);
      setError(getAppointmentApiErrorMessage(requestError, t('pages:appointments.errors.updateAppointment')));
    } finally {
      setAppointmentUpdating(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment || !canDeleteAppointments) {
      setError(t('pages:appointments.accessDeniedDelete'));
      return;
    }
    setAppointmentCancelling(true);
    setError(null);
    setSuccess(null);
    try {
      const cancelled = await cancelAppointment(selectedAppointment.id, {
        reason: cancellationReason.trim(),
      });
      setSelectedAppointment(cancelled);
      await loadAppointments(appointmentsRange);
      setSuccess(t('pages:appointments.calendar.cancelSuccess'));
    } catch (requestError: unknown) {
      console.error('Error cancelling appointment:', requestError);
      setError(getAppointmentApiErrorMessage(requestError, t('pages:appointments.errors.cancelAppointment')));
    } finally {
      setAppointmentCancelling(false);
    }
  };

  const syncWeeklyWindows = useCallback(async (calendarId: string, existingRules: AvailabilityRule[]) => {
    const payloadsByWeekday = buildWeeklyWindowPayloads(weeklyWindows);

    for (const weekday of WEEKDAY_VALUES) {
      const activeRules = existingRules
        .filter((rule) => rule.isActive && Number(rule.weekday) === weekday)
        .sort(sortWeekdayRules);
      const desiredPayloads = payloadsByWeekday.get(weekday) || [];

      for (let index = 0; index < desiredPayloads.length; index += 1) {
        const payload = desiredPayloads[index];
        const existingRule = activeRules[index];
        if (existingRule) {
          await updateAvailabilityRule(existingRule.id, payload);
        } else {
          await createAvailabilityRule(calendarId, payload);
        }
      }

      for (const extraRule of activeRules.slice(desiredPayloads.length)) {
        await updateAvailabilityRule(extraRule.id, { isActive: false });
      }
    }
  }, [weeklyWindows]);

  /**
   * Validates the simplified weekly-window editor before syncing it to per-day
   * availability rules. Empty days or inverted time ranges would otherwise fail
   * later at the API boundary with a less actionable message.
   */
  const validateWeeklyWindows = useCallback(() => {
    for (let index = 0; index < weeklyWindows.length; index += 1) {
      const windowEntry = weeklyWindows[index];
      const number = index + 1;
      if (windowEntry.days.length === 0) {
        return t('pages:appointments.rules.daysRequired', { number });
      }
      if (!windowEntry.startTime || !windowEntry.endTime) {
        return t('pages:appointments.rules.timeRequired', { number });
      }
      if (windowEntry.startTime >= windowEntry.endTime) {
        return t('pages:appointments.rules.invalidRange', { number });
      }
    }
    return null;
  }, [t, weeklyWindows]);

  const handleSaveCalendar = async () => {
    if (!canManageCalendars) {
      setError(t('pages:appointments.accessDeniedManage'));
      return;
    }
    if (!form.name.trim()) {
      setError(t('pages:appointments.errors.nameRequired'));
      return;
    }
    const weeklyWindowError = validateWeeklyWindows();
    if (weeklyWindowError) {
      setError(weeklyWindowError);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = buildCalendarPayload(form);
      const saved = isNew || !id
        ? await createAppointmentCalendar(payload)
        : await updateAppointmentCalendar(id, payload);
      await syncWeeklyWindows(saved.id, rules);
      const updatedRules = await listAvailabilityRules(saved.id);
      setCalendar(saved);
      setForm(calendarToForm(saved));
      setRules(updatedRules);
      setWeeklyWindows(buildWeeklyWindowsFromRules(updatedRules));
      setSuccess(t('pages:appointments.form.saveSuccess'));
      if (isNew) {
        navigate(`/appointments/${saved.id}`, { replace: true });
      }
    } catch (requestError: unknown) {
      console.error('Error saving appointment calendar:', requestError);
      setError(getAppointmentApiErrorMessage(requestError, t('pages:appointments.errors.save')));
    } finally {
      setSaving(false);
    }
  };

  const openCloneDialog = () => {
    if (!calendar) {
      return;
    }
    setError(null);
    setSuccess(null);
    setCloneDialog({
      open: true,
      name: t('pages:appointments.clone.defaultName', { name: calendar.name }),
      includeFutureExceptions: true,
      includeBlocks: true,
    });
  };

  const closeCloneDialog = () => {
    if (cloning) {
      return;
    }
    setCloneDialog(createClosedCloneDialogState());
  };

  const handleCloneCalendar = async () => {
    if (!id || !canManageCalendars) {
      setError(t('pages:appointments.accessDeniedManage'));
      return;
    }
    const name = cloneDialog.name.trim();
    if (!name) {
      setError(t('pages:appointments.errors.nameRequired'));
      return;
    }

    setCloning(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: AppointmentCalendarClonePayload = {
        name,
        includeFutureExceptions: cloneDialog.includeFutureExceptions,
        includeBlocks: cloneDialog.includeBlocks,
      };
      const clonedCalendar = await cloneAppointmentCalendar(id, payload);
      setCloneDialog(createClosedCloneDialogState());
      navigate(`/appointments/${clonedCalendar.id}`);
    } catch (requestError: unknown) {
      console.error('Error cloning appointment calendar:', requestError);
      setError(getAppointmentApiErrorMessage(requestError, t('pages:appointments.errors.clone')));
    } finally {
      setCloning(false);
    }
  };

  const handleWeeklyWindowDayToggle = (entryId: string, weekday: Weekday) => {
    setWeeklyWindows((current) => current.map((windowEntry) => {
      if (windowEntry.id !== entryId) {
        return windowEntry;
      }
      const nextDays = windowEntry.days.includes(weekday)
        ? windowEntry.days.filter((day) => day !== weekday)
        : [...windowEntry.days, weekday].sort((left, right) => left - right);
      return { ...windowEntry, days: nextDays };
    }));
  };

  const handleWeeklyWindowTimeChange = (
    entryId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ) => {
    setWeeklyWindows((current) => current.map((windowEntry) => (
      windowEntry.id === entryId ? { ...windowEntry, [field]: value } : windowEntry
    )));
  };

  const handleAddWeeklyWindow = () => {
    const usedDays = new Set(weeklyWindows.flatMap((windowEntry) => windowEntry.days));
    const firstAvailableDay = WEEKDAY_VALUES.find((weekday) => !usedDays.has(weekday));
    setWeeklyWindows((current) => [
      ...current,
      createWeeklyWindowEntry(firstAvailableDay === undefined ? [] : [firstAvailableDay]),
    ]);
  };

  const handleDeleteWeeklyWindow = (entryId: string) => {
    setWeeklyWindows((current) => current.filter((windowEntry) => windowEntry.id !== entryId));
  };

  const handleResetWeeklyWindows = () => {
    setWeeklyWindows(createDefaultWeeklyWindows());
  };

  const openNewExceptionDialog = (exceptionType: AvailabilityExceptionType) => {
    setExceptionDialog({
      open: true,
      exception: null,
      values: { ...createExceptionPayload(), exceptionType },
    });
  };

  const openEditExceptionDialog = (entry: AvailabilityException) => {
    setExceptionDialog({
      open: true,
      exception: entry,
      values: {
        date: entry.date,
        startTime: entry.startTime,
        endTime: entry.endTime,
        exceptionType: entry.exceptionType,
        reason: entry.reason,
      },
    });
  };

  const handleSaveException = async () => {
    if (!id || !canManageBlocks) {
      return;
    }
    setError(null);
    try {
      const values = exceptionDialog.values;
      const payload: AvailabilityExceptionPayload = values.exceptionType === 'blocked'
        ? {
          ...values,
          startTime: values.startTime || null,
          endTime: values.endTime || null,
        }
        : values;
      if (exceptionDialog.exception) {
        await updateAvailabilityException(exceptionDialog.exception.id, payload);
      } else {
        await createAvailabilityException(id, payload);
      }
      setExceptionDialog({ open: false, exception: null, values: createExceptionPayload() });
      await loadExceptions();
    } catch (requestError: unknown) {
      console.error('Error saving appointment exception:', requestError);
      setError(t('pages:appointments.errors.saveException'));
    }
  };

  const handleDeleteException = async (entry: AvailabilityException) => {
    if (!canManageBlocks || !window.confirm(t('pages:appointments.exceptions.deleteConfirm'))) {
      return;
    }
    try {
      await deleteAvailabilityException(entry.id);
      await loadExceptions();
    } catch (requestError: unknown) {
      console.error('Error deleting appointment exception:', requestError);
      setError(t('pages:appointments.errors.deleteException'));
    }
  };

  const formatAppointmentDateTime = useCallback((value: string) => (
    new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value))
  ), [locale]);

  const formatAppointmentTime = useCallback((value: string) => (
    new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value))
  ), [locale]);

  const appointmentEvents = useMemo(() => appointments.map((appointment) => ({
    id: appointment.id,
    title: getAppointmentDisplayName(appointment, t('pages:appointments.calendar.untitled')),
    start: appointment.startsAt,
    end: appointment.endsAt,
    classNames: [
      'appointment-event',
      `appointment-event-${appointment.status}`,
    ],
  })), [appointments, t]);
  const fullCalendarPlugins = useMemo(() => [dayGridPlugin, interactionPlugin], []);
  const fullCalendarLocales = useMemo(() => [ptBrLocale, esLocale], []);
  const fullCalendarHeaderToolbar = useMemo(() => ({
    left: 'prev,next today',
    center: 'title',
    right: '',
  }), []);
  const fullCalendarEventTimeFormat = useMemo(() => ({
    hour: 'numeric' as const,
    minute: '2-digit' as const,
    meridiem: useTwelveHourClock ? 'short' as const : false,
  }), [useTwelveHourClock]);

  if (!canRead && !isNew) {
    return <Alert severity="warning">{t('pages:appointments.accessDenied')}</Alert>;
  }
  if (isNew && !canManageCalendars) {
    return <Alert severity="warning">{t('pages:appointments.accessDeniedManage')}</Alert>;
  }

  const headerTitle = isNew
    ? t('pages:appointments.form.newTitle')
    : calendar?.name || t('pages:appointments.form.editTitle');

  const breadcrumbs = [
    { label: t('pages:appointments.title'), to: '/appointments' },
    { label: isNew ? t('pages:appointments.new') : t('pages:appointments.form.editTitle') },
  ];

  const sectionIcon = <CalendarMonth sx={{ fontSize: 32, color: 'primary.main' }} />;

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {!isNew && (
        <Button variant="outlined" startIcon={<Refresh />} onClick={() => {
          loadCalendar();
          loadRules();
          loadExceptions();
          loadAppointments(appointmentsRange);
        }}>
          {t('common:actions.refresh')}
        </Button>
      )}
      {!isNew && canManageCalendars && (
        <Button
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={openCloneDialog}
          disabled={saving || loading || cloning}
        >
          {t('pages:appointments.clone.button')}
        </Button>
      )}
      {canManageCalendars && (
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSaveCalendar}
          disabled={saving || loading || cloning}
        >
          {saving ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
      )}
    </Box>
  );

  const calendarTabContent = (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">{t('pages:appointments.calendar.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:appointments.calendar.description')}
          </Typography>
        </Box>
        {!isNew && canCreateAppointments ? (
          <Button
            variant="contained"
            startIcon={<EventAvailable />}
            onClick={() => openAppointmentDialog()}
            disabled={appointmentSaving}
          >
            {t('pages:appointments.calendar.create')}
          </Button>
        ) : null}
      </Box>

      {isNew ? (
        <Alert severity="info">{t('pages:appointments.calendar.saveFirst')}</Alert>
      ) : !canReadAppointments ? (
        <Alert severity="warning">{t('pages:appointments.accessDeniedAppointments')}</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 2fr) minmax(320px, 0.9fr)' },
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              '& .fc': { color: 'text.primary' },
              '& .fc .fc-toolbar': {
                gap: 1,
                flexWrap: 'wrap',
              },
              '& .fc .fc-toolbar-title': {
                fontSize: { xs: '1rem', sm: '1.15rem' },
                fontWeight: 700,
                letterSpacing: 0,
              },
              '& .fc .fc-button': {
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 700,
              },
              '& .fc .fc-scrollgrid, & .fc .fc-scrollgrid-section > td, & .fc .fc-col-header-cell, & .fc .fc-daygrid-day':
                {
                  borderColor: 'divider',
                },
              '& .fc .fc-day-today': {
                bgcolor: 'action.hover',
              },
              '& .fc .fc-col-header-cell-cushion': {
                color: 'text.secondary',
                fontWeight: 700,
                py: 0.75,
              },
              '& .fc .fc-daygrid-day-frame': {
                minHeight: { xs: 92, md: 118 },
              },
              '& .fc .fc-daygrid-day-number': {
                color: 'text.primary',
                fontWeight: 600,
                p: 0.75,
              },
              '& .fc .fc-daygrid-more-link': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '& .fc .fc-daygrid-event': {
                borderRadius: 0.75,
                fontSize: '0.75rem',
                lineHeight: 1.35,
                px: 0.5,
                py: 0.125,
              },
              '& .fc .appointment-event': {
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'primary.contrastText',
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.5,
              },
              '& .fc .appointment-event-cancelled': {
                bgcolor: 'text.disabled',
                borderColor: 'text.disabled',
                opacity: 0.7,
                textDecoration: 'line-through',
              },
            }}
          >
            <FullCalendar
              plugins={fullCalendarPlugins}
              locales={fullCalendarLocales}
              locale={fullCalendarLocale}
              initialView="dayGridMonth"
              headerToolbar={fullCalendarHeaderToolbar}
              height="auto"
              nowIndicator
              dayMaxEventRows={3}
              displayEventEnd
              eventDisplay="block"
              selectable={canCreateAppointments}
              editable={false}
              events={appointmentEvents}
              datesSet={handleCalendarDatesSet}
              dateClick={handleAppointmentDateClick}
              eventClick={handleAppointmentEventClick}
              eventTimeFormat={fullCalendarEventTimeFormat}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {t('pages:appointments.calendar.visiblePeriod')}
              </Typography>
              {appointmentsLoading ? <CircularProgress size={18} /> : null}
            </Box>
            {appointments.length === 0 && !appointmentsLoading ? (
              <Typography color="text.secondary">{t('pages:appointments.calendar.empty')}</Typography>
            ) : (
              <Stack spacing={1}>
                {appointments.map((appointment) => (
                  <Paper
                    key={appointment.id}
                    variant="outlined"
                    onClick={() => openAppointmentDetails(appointment)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      transition: 'border-color 120ms ease, background-color 120ms ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          color={getAppointmentStatusColor(appointment.status)}
                          label={t(`pages:appointments.calendar.status.${appointment.status}`)}
                        />
                        <Typography variant="body2" fontWeight={700}>
                          {formatAppointmentTime(appointment.startsAt)} - {formatAppointmentTime(appointment.endsAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {getAppointmentDisplayName(appointment, t('pages:appointments.calendar.untitled'))}
                      </Typography>
                      {appointment.contactPhoneE164 ? (
                        <Typography variant="caption" color="text.secondary">
                          {appointment.contactPhoneE164}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );

  const generalTabContent = (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
      }}
    >
      <TextField
        required
        label={t('pages:appointments.form.name')}
        value={form.name}
        disabled={!canManageCalendars || loading}
        onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
      />
      <TextField
        label={t('pages:appointments.form.timezone')}
        value={form.timezone}
        disabled={!canManageCalendars || loading}
        onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))}
      />
      <TextField
        label={t('pages:appointments.form.description')}
        value={form.description}
        disabled={!canManageCalendars || loading}
        multiline
        minRows={3}
        sx={{ gridColumn: { md: '1 / -1' } }}
        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
      />
      <FormControlLabel
        control={
          <Switch
            checked={form.isActive}
            disabled={!canManageCalendars || loading}
            onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
          />
        }
        label={t('pages:appointments.form.active')}
      />
    </Box>
  );

  const slotPolicyTabContent = (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
      }}
    >
      <TextField
        type="number"
        label={
          <FieldLabelWithHelp
            label={t('pages:appointments.form.minSlot')}
            help={t('pages:appointments.form.minSlotHelp')}
          />
        }
        value={form.minSlotDurationMinutes}
        disabled={!canManageCalendars || loading}
        inputProps={SLOT_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          minSlotDurationMinutes: Number(event.target.value),
        }))}
      />
      <TextField
        type="number"
        label={
          <FieldLabelWithHelp
            label={t('pages:appointments.form.defaultSlot')}
            help={t('pages:appointments.form.defaultSlotHelp')}
          />
        }
        value={form.defaultSlotDurationMinutes}
        disabled={!canManageCalendars || loading}
        inputProps={SLOT_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          defaultSlotDurationMinutes: Number(event.target.value),
        }))}
      />
      <TextField
        type="number"
        label={
          <FieldLabelWithHelp
            label={t('pages:appointments.form.maxSlot')}
            help={t('pages:appointments.form.maxSlotHelp')}
          />
        }
        value={form.maxSlotDurationMinutes}
        disabled={!canManageCalendars || loading}
        inputProps={SLOT_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          maxSlotDurationMinutes: Number(event.target.value),
        }))}
      />
      <TextField
        type="number"
        label={
          <FieldLabelWithHelp
            label={t('pages:appointments.form.slotStep')}
            help={t('pages:appointments.form.slotStepHelp')}
          />
        }
        value={form.slotStepMinutes}
        disabled={!canManageCalendars || loading}
        inputProps={SLOT_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          slotStepMinutes: Number(event.target.value),
        }))}
      />
      <TextField
        type="number"
        label={t('pages:appointments.form.minNotice')}
        value={form.minNoticeMinutes}
        disabled={!canManageCalendars || loading}
        inputProps={NOTICE_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          minNoticeMinutes: Number(event.target.value),
        }))}
      />
      <TextField
        type="number"
        label={t('pages:appointments.form.maxDaysAhead')}
        value={form.maxDaysAhead}
        disabled={!canManageCalendars || loading}
        inputProps={DAYS_AHEAD_INPUT_PROPS}
        onChange={(event) => setForm((current) => ({
          ...current,
          maxDaysAhead: Number(event.target.value),
        }))}
      />
      <TextField
        label={t('pages:appointments.form.capacity')}
        value="1"
        disabled
      />
    </Box>
  );

  const weeklyWindowsTabContent = (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{t('pages:appointments.rules.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pages:appointments.rules.description')}
        </Typography>
      </Box>

      {rulesLoading ? (
        <Typography color="text.secondary">{t('common:common.loading')}</Typography>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
          <Stack spacing={2}>
            {weeklyWindows.length === 0 ? (
              <Alert severity="warning">{t('pages:appointments.rules.empty')}</Alert>
            ) : null}
            {weeklyWindows.map((windowEntry, index) => (
              <Paper
                key={windowEntry.id}
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: windowEntry.days.length > 0 ? 'background.paper' : 'action.hover',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('pages:appointments.rules.window', { number: index + 1 })}
                  </Typography>
                  {canManageCalendars ? (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteWeeklyWindow(windowEntry.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('pages:appointments.rules.days')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {WEEKDAY_VALUES.map((weekday) => {
                      const selected = windowEntry.days.includes(weekday);
                      return (
                        <Chip
                          key={weekday}
                          size="small"
                          label={weekdayLabel(weekday)}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={canManageCalendars
                            ? () => handleWeeklyWindowDayToggle(windowEntry.id, weekday)
                            : undefined}
                          sx={{ cursor: canManageCalendars ? 'pointer' : 'default' }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TimePicker
                    label={t('pages:appointments.rules.startTime')}
                    value={windowEntry.startTime ? dayjs(`2000-01-01T${windowEntry.startTime}`) : null}
                    disabled={!canManageCalendars}
                    ampm={useTwelveHourClock}
                    onChange={(value: Dayjs | null) => {
                      handleWeeklyWindowTimeChange(windowEntry.id, 'startTime', value ? value.format('HH:mm') : '');
                    }}
                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    -
                  </Typography>
                  <TimePicker
                    label={t('pages:appointments.rules.endTime')}
                    value={windowEntry.endTime ? dayjs(`2000-01-01T${windowEntry.endTime}`) : null}
                    disabled={!canManageCalendars}
                    ampm={useTwelveHourClock}
                    onChange={(value: Dayjs | null) => {
                      handleWeeklyWindowTimeChange(windowEntry.id, 'endTime', value ? value.format('HH:mm') : '');
                    }}
                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                  />
                </Box>
              </Paper>
            ))}
            {canManageCalendars ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddWeeklyWindow}>
                  {t('pages:appointments.rules.add')}
                </Button>
                <Button variant="text" color="secondary" startIcon={<RestartAlt />} onClick={handleResetWeeklyWindows}>
                  {t('pages:appointments.rules.resetDefaults')}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </LocalizationProvider>
      )}
    </Stack>
  );

  const exceptionsTabContent = (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6">{t('pages:appointments.exceptions.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:appointments.exceptions.description')}
          </Typography>
        </Box>
        {!isNew && canManageBlocks ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<EventBusy />} onClick={() => openNewExceptionDialog('blocked')}>
              {t('pages:appointments.exceptions.addBlock')}
            </Button>
            <Button startIcon={<Schedule />} onClick={() => openNewExceptionDialog('extra_available')}>
              {t('pages:appointments.exceptions.addExtra')}
            </Button>
          </Box>
        ) : null}
      </Box>

      {isNew ? (
        <Alert severity="info">{t('pages:appointments.exceptions.saveFirst')}</Alert>
      ) : exceptionsLoading ? (
        <Typography color="text.secondary">{t('common:common.loading')}</Typography>
      ) : exceptions.length === 0 ? (
        <Typography color="text.secondary">{t('pages:appointments.exceptions.empty')}</Typography>
      ) : (
        <Stack spacing={1}>
          {exceptions.map((entry) => (
            <Paper key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      color={entry.exceptionType === 'blocked' ? 'warning' : 'info'}
                      label={t(`pages:appointments.exceptions.type.${entry.exceptionType}`)}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {entry.date}
                    </Typography>
                    {entry.startTime && entry.endTime ? (
                      <Typography variant="body2" color="text.secondary">
                        {entry.startTime} - {entry.endTime}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('pages:appointments.exceptions.fullDay')}
                      </Typography>
                    )}
                  </Box>
                  {entry.reason ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {entry.reason}
                    </Typography>
                  ) : null}
                </Box>
                {canManageBlocks ? (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => openEditExceptionDialog(entry)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteException(entry)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ) : null}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );

  const formTabs: ButtonTab[] = [
    {
      id: 'calendar',
      label: t('pages:appointments.calendar.title'),
      icon: <CalendarMonth fontSize="small" />,
      content: calendarTabContent,
    },
    {
      id: 'general',
      label: t('pages:appointments.form.general'),
      icon: <InfoOutlined fontSize="small" />,
      content: generalTabContent,
    },
    {
      id: 'slot-policy',
      label: t('pages:appointments.form.slotPolicy'),
      icon: <Tune fontSize="small" />,
      content: slotPolicyTabContent,
    },
    {
      id: 'weekly-windows',
      label: t('pages:appointments.rules.title'),
      icon: <Schedule fontSize="small" />,
      content: weeklyWindowsTabContent,
    },
    {
      id: 'exceptions',
      label: t('pages:appointments.exceptions.title'),
      icon: <EventBusy fontSize="small" />,
      content: exceptionsTabContent,
    },
  ];
  const exceptionDialogIsBlock = exceptionDialog.values.exceptionType === 'blocked';
  const exceptionDialogIcon = exceptionDialogIsBlock
    ? <EventBusy color="warning" />
    : <Schedule color="info" />;
  const exceptionDialogTitle = exceptionDialogIsBlock
    ? t(exceptionDialog.exception ? 'pages:appointments.exceptions.editBlock' : 'pages:appointments.exceptions.addBlock')
    : t(exceptionDialog.exception ? 'pages:appointments.exceptions.editExtra' : 'pages:appointments.exceptions.addExtra');
  const exceptionDialogHelp = exceptionDialogIsBlock
    ? t('pages:appointments.exceptions.help.blocked')
    : t('pages:appointments.exceptions.help.extra_available');
  const selectedAppointmentIsScheduled = selectedAppointment?.status === 'scheduled';
  const selectedAppointmentCanEdit = Boolean(selectedAppointment && canUpdateAppointments && selectedAppointmentIsScheduled);
  const selectedAppointmentCanCancel = Boolean(selectedAppointment && canDeleteAppointments && selectedAppointmentIsScheduled);

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={sectionIcon}
        entityId={calendar?.id ?? null}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/appointments"
      />

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <ButtonTabs
            tabs={formTabs}
            defaultTab={isNew ? 'general' : 'calendar'}
            tabParam="tab"
            useUrl
            scrollable
            tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
            tabButtonSx={{ px: 1.5, py: 0.4, minHeight: 36 }}
            contentSx={{ mt: 3 }}
          />
        </CardContent>
      </Card>

      <Dialog open={cloneDialog.open} onClose={closeCloneDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('pages:appointments.clone.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('pages:appointments.clone.description')}
            </Typography>
            <TextField
              required
              autoFocus
              label={t('pages:appointments.form.name')}
              value={cloneDialog.name}
              disabled={cloning}
              onChange={(event) => setCloneDialog((current) => ({
                ...current,
                name: event.target.value,
              }))}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={cloneDialog.includeFutureExceptions}
                  disabled={cloning}
                  onChange={(event) => setCloneDialog((current) => ({
                    ...current,
                    includeFutureExceptions: event.target.checked,
                  }))}
                />
              )}
              label={t('pages:appointments.clone.includeFutureExceptions')}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={cloneDialog.includeBlocks}
                  disabled={cloning}
                  onChange={(event) => setCloneDialog((current) => ({
                    ...current,
                    includeBlocks: event.target.checked,
                  }))}
                />
              )}
              label={t('pages:appointments.clone.includeBlocks')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCloneDialog} disabled={cloning}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopy />}
            onClick={handleCloneCalendar}
            disabled={cloning || !cloneDialog.name.trim()}
          >
            {cloning ? t('pages:appointments.clone.cloning') : t('pages:appointments.clone.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={appointmentDialog.open} onClose={closeAppointmentDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventAvailable color="primary" />
          {t('pages:appointments.calendar.create')}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  type="date"
                  label={t('pages:appointments.calendar.date')}
                  value={appointmentDialog.date}
                  InputLabelProps={{ shrink: true }}
                  disabled={appointmentSaving}
                  onChange={(event) => setAppointmentDialog((current) => ({
                    ...current,
                    date: event.target.value,
                  }))}
                />
                <TimePicker
                  label={t('pages:appointments.calendar.startTime')}
                  value={appointmentDialog.startTime ? dayjs(`2000-01-01T${appointmentDialog.startTime}`) : null}
                  disabled={appointmentSaving}
                  ampm={useTwelveHourClock}
                  onChange={(value: Dayjs | null) => setAppointmentDialog((current) => ({
                    ...current,
                    startTime: value ? value.format('HH:mm') : '',
                  }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
              <TextField
                type="number"
                label={t('pages:appointments.calendar.duration')}
                value={appointmentDialog.durationMinutes}
                disabled={appointmentSaving}
                inputProps={SLOT_INPUT_PROPS}
                onChange={(event) => setAppointmentDialog((current) => ({
                  ...current,
                  durationMinutes: Number(event.target.value),
                }))}
              />
              <TextField
                label={t('pages:appointments.calendar.contactName')}
                value={appointmentDialog.contactName}
                disabled={appointmentSaving}
                onChange={(event) => setAppointmentDialog((current) => ({
                  ...current,
                  contactName: event.target.value,
                }))}
              />
              <TextField
                label={t('pages:appointments.calendar.contactPhone')}
                value={appointmentDialog.contactPhoneE164}
                disabled={appointmentSaving}
                onChange={(event) => setAppointmentDialog((current) => ({
                  ...current,
                  contactPhoneE164: event.target.value,
                }))}
              />
              <TextField
                label={t('pages:appointments.calendar.contactEmail')}
                value={appointmentDialog.contactEmail}
                disabled={appointmentSaving}
                onChange={(event) => setAppointmentDialog((current) => ({
                  ...current,
                  contactEmail: event.target.value,
                }))}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAppointmentDialog} disabled={appointmentSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={appointmentSaving ? <CircularProgress size={18} color="inherit" /> : <EventAvailable />}
            onClick={handleCreateAppointment}
            disabled={appointmentSaving || !appointmentDialog.date || !appointmentDialog.startTime}
          >
            {appointmentSaving ? t('common:actions.saving') : t('pages:appointments.calendar.createSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedAppointment)}
        onClose={() => setSelectedAppointment(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CalendarMonth color="primary" />
          <Box component="span" sx={{ flex: 1, minWidth: 180 }}>
            {t('pages:appointments.calendar.details')}
          </Box>
          {selectedAppointment ? (
            <Chip
              size="small"
              color={getAppointmentStatusColor(selectedAppointment.status)}
              label={t(`pages:appointments.calendar.status.${selectedAppointment.status}`)}
            />
          ) : null}
        </DialogTitle>
        <DialogContent>
          {selectedAppointment ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {formatAppointmentDateTime(selectedAppointment.startsAt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatAppointmentTime(selectedAppointment.startsAt)} - {formatAppointmentTime(selectedAppointment.endsAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label={t('pages:appointments.calendar.contactName')}
                  value={appointmentDetailsForm.contactName}
                  disabled={!selectedAppointmentCanEdit || appointmentUpdating}
                  InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  onChange={(event) => setAppointmentDetailsForm((current) => ({
                    ...current,
                    contactName: event.target.value,
                  }))}
                />
                <TextField
                  label={t('pages:appointments.calendar.contactPhone')}
                  value={appointmentDetailsForm.contactPhoneE164}
                  disabled={!selectedAppointmentCanEdit || appointmentUpdating}
                  InputProps={{ startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  onChange={(event) => setAppointmentDetailsForm((current) => ({
                    ...current,
                    contactPhoneE164: event.target.value,
                  }))}
                />
                <TextField
                  label={t('pages:appointments.calendar.contactEmail')}
                  value={appointmentDetailsForm.contactEmail}
                  disabled={!selectedAppointmentCanEdit || appointmentUpdating}
                  sx={{ gridColumn: { sm: '1 / -1' } }}
                  onChange={(event) => setAppointmentDetailsForm((current) => ({
                    ...current,
                    contactEmail: event.target.value,
                  }))}
                />
              </Box>
              {selectedAppointment.createdByUsername ? (
                <Typography variant="caption" color="text.secondary">
                  {t('pages:appointments.calendar.createdBy', { value: selectedAppointment.createdByUsername })}
                </Typography>
              ) : null}
              {selectedAppointment.callId ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('pages:appointments.calendar.linkedCall')}
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={`/calls/${selectedAppointment.callId}`}
                    variant="body2"
                    underline="hover"
                    sx={{
                      alignItems: 'center',
                      color: 'primary.main',
                      display: 'inline-flex',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      gap: 0.75,
                      wordBreak: 'break-all',
                    }}
                  >
                    <Phone sx={{ fontSize: 18 }} />
                    {selectedAppointment.callId}
                  </Link>
                </Box>
              ) : null}
              {selectedAppointmentCanEdit ? (
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={appointmentUpdating ? <CircularProgress size={18} /> : <Save />}
                    onClick={handleUpdateAppointment}
                    disabled={appointmentUpdating}
                  >
                    {appointmentUpdating ? t('common:actions.saving') : t('common:actions.save')}
                  </Button>
                </Box>
              ) : null}
              {selectedAppointmentCanCancel ? (
                <>
                  <Divider />
                  <TextField
                    label={t('pages:appointments.calendar.cancelReason')}
                    value={cancellationReason}
                    disabled={appointmentCancelling}
                    multiline
                    minRows={2}
                    onChange={(event) => setCancellationReason(event.target.value)}
                  />
                </>
              ) : null}
              {selectedAppointment.cancellationReason ? (
                <Typography variant="body2" color="text.secondary">
                  {t('pages:appointments.calendar.cancelledReason', {
                    value: selectedAppointment.cancellationReason,
                  })}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAppointment(null)} disabled={appointmentCancelling || appointmentUpdating}>
            {t('common:actions.close')}
          </Button>
          {selectedAppointmentCanCancel ? (
            <Button
              color="error"
              variant="contained"
              startIcon={appointmentCancelling ? <CircularProgress size={18} color="inherit" /> : <Cancel />}
              onClick={handleCancelAppointment}
              disabled={appointmentCancelling}
            >
              {appointmentCancelling ? t('common:actions.saving') : t('pages:appointments.calendar.cancel')}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={exceptionDialog.open} onClose={() => setExceptionDialog((current) => ({ ...current, open: false }))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {exceptionDialogIcon}
          <Box component="span" sx={{ flex: 1 }}>
            {exceptionDialogTitle}
          </Box>
          <Tooltip title={exceptionDialogHelp} arrow>
            <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {exceptionDialogHelp}
            </Typography>
            <TextField
              type="date"
              label={t('pages:appointments.exceptions.date')}
              value={exceptionDialog.values.date}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setExceptionDialog((current) => ({
                ...current,
                values: { ...current.values, date: event.target.value },
              }))}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                type="time"
                label={t('pages:appointments.exceptions.startTime')}
                value={exceptionDialog.values.startTime || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setExceptionDialog((current) => ({
                  ...current,
                  values: { ...current.values, startTime: event.target.value || null },
                }))}
              />
              <TextField
                type="time"
                label={t('pages:appointments.exceptions.endTime')}
                value={exceptionDialog.values.endTime || ''}
                InputLabelProps={{ shrink: true }}
                onChange={(event) => setExceptionDialog((current) => ({
                  ...current,
                  values: { ...current.values, endTime: event.target.value || null },
                }))}
              />
            </Box>
            <TextField
              label={t('pages:appointments.exceptions.reason')}
              value={exceptionDialog.values.reason || ''}
              multiline
              minRows={2}
              onChange={(event) => setExceptionDialog((current) => ({
                ...current,
                values: { ...current.values, reason: event.target.value },
              }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExceptionDialog((current) => ({ ...current, open: false }))}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveException}>
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
